import { ChevronDownIcon, PlusIcon, Sort as SortIcon } from 'components/icons';
import FeatureFlagged from 'components/featureflag/FeatureFlagged';
import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { Tooltip } from 'components/ui/tooltip';
import HoursDisplay from 'components/util/HoursDisplay';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { INVOICE_TAB_PATHS } from 'routes/constants';
import { Ref, useCallback, useId, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useUpdateInvoiceTimecardMutation } from 'store/api/invoice-details';
import { FeatureFlag } from 'types/features';
import { InvoiceTimecard, InvoiceTimecardAssignment } from 'types/invoice-details';
import { t } from 'utils/getTranslations';
import { createTabState } from 'utils/tabStateUtil';
import InvoiceLoader from '../../components/InvoiceLoader';
import { useTimecardActions } from '../../components/TimecardActions';
import {
    AdvancedBillingToggle,
    CaseLabel,
    ITEM_FIGURE,
    ITEM_SUB_FIGURE,
    ITEM_TOTALS_ROW,
    ItemBulkSelectButton,
    ItemCard,
    ItemHeadCells,
    ItemTable,
    TITLE_ACTION,
    useIsNarrowCard,
} from '../InvoiceItemTable';
import { CasePricelistRangeTooltip } from './CasePricelistRangeTooltip';
import InvoiceAssignmentTimecardHeader from './InvoiceAssignmentTimecardHeader';
import { sortTimecardsByField } from './InvoiceTimecardAssignmentTable';
import {
    buildElementKey,
    DEFAULT_SORT,
    InvoiceTimecardTableProps,
    useInvoiceTimecardTable,
} from './InvoiceTimecardTable';
import { SortField, TimecardSortState } from './InvoiceTimecardTableTotals';
import { InvoiceTimecardTableRow } from './InvoiceTimecardTableRow';

const SORT_FIELDS: { field: SortField; labelKey: string }[] = [
    { field: 'date', labelKey: 'invoices.details.table.date' },
    { field: 'author', labelKey: 'invoices.details.table.author' },
    { field: 'billable', labelKey: 'invoices.details.table.sort-billable' },
];

/** One line of the windowed body: an assignment's header, a timecard, an assignment's totals, its Add timecard. */
type Line =
    | { kind: 'group' | 'totals' | 'add'; assignment: InvoiceTimecardAssignment }
    | { kind: 'row'; timecard: InvoiceTimecard };

const lineKey = (line: Line) =>
    line.kind === 'row' ? `row-${line.timecard.id}` : `${line.kind}-${line.assignment.assignmentId}`;

/** An assignment's or the card's billed and used hours and amount. */
const sumAssignments = (assignments: InvoiceTimecardAssignment[], isPaymentCurrencyMode: boolean) =>
    assignments.reduce(
        (sum, a) => ({
            billed: sum.billed + (a.billedHours ?? 0),
            used: sum.used + (a.usedHours ?? 0),
            amount:
                sum.amount + ((isPaymentCurrencyMode ? a.totalAmountPaymentCurrency : a.totalAfterAdjustments) ?? 0),
        }),
        { billed: 0, used: 0, amount: 0 }
    );

/**
 * A case's timecards in the new design: a card titled by its case, its rows a windowed table with one sort for every
 * assignment group, and the selection's actions in place of the title row while rows are ticked. With no timecards
 * it keeps its title and says so, with Add timecard where one can be added.
 */
export function InvoiceTimecardsCard(props: InvoiceTimecardTableProps) {
    const {
        addedInvoiceTimecardIds,
        isInViewMode,
        invoice,
        isLoading,
        classifiers,
        validateForm,
        setValue,
        getValues,
        reset,
        control,
        invoiceMatter,
    } = props;
    const table = useInvoiceTimecardTable(props);
    const {
        advancedBillingEnabled,
        setAdvancedBillingEnabled,
        openModalForTimecardId,
        clearModalRequest,
        navigateWithReturn,
        canCreateTimecards,
        isCorrectionInvoice,
        isCredit,
        isSent,
        isPaid,
        buttonsDisabled,
        selectedIds,
        clearSelection,
        onBulkSelect,
        invoiceTimecardTotalCount,
        invoiceId,
        matterId,
        allowsNewTimecards,
        isPaymentCurrencyMode,
        displayCurrency,
    } = table;
    const actions = useTimecardActions({
        ...table,
        handleMoveTimecard: table.handleMove,
        getValues,
        invoiceAllowsNewTimecards: allowsNewTimecards,
        userCanCreateTimecards: canCreateTimecards,
        invoice,
    });
    const [sortState, setSortState] = useState<TimecardSortState>(DEFAULT_SORT);
    const { cardRef, isNarrow } = useIsNarrowCard(advancedBillingEnabled);
    const titleId = useId();
    const invoiceTimecards = useWatch({ control, name: 'invoiceTimecards' });
    const [updateInvoiceTimecard] = useUpdateInvoiceTimecardMutation();
    const intl = useIntl();

    const formIndexById = useMemo(
        () => new Map((invoiceTimecards ?? []).map((timecard, index): [number, number] => [timecard.id, index])),
        [invoiceTimecards]
    );

    const assignments = useMemo(
        () =>
            invoiceMatter.invoiceAssignments
                .filter((assignment) => assignment.timeCardDetails?.length)
                .toSorted((a, b) =>
                    (a.assignmentName ?? '').localeCompare(b.assignmentName ?? '', undefined, { numeric: true })
                ),
        [invoiceMatter.invoiceAssignments]
    );

    // A row waits until the form holds its timecard: a refetched summary carries new rows a render before the form.
    const lines = useMemo<Line[]>(
        () =>
            assignments.flatMap((assignment): Line[] => {
                const rows: Line[] = sortTimecardsByField(assignment.timeCardDetails, sortState)
                    .filter((timecard) => formIndexById.has(timecard.id))
                    .map((timecard) => ({ kind: 'row' as const, timecard }));
                if (!assignment.assignmentId || isCredit) return rows;
                return [
                    { kind: 'group', assignment },
                    ...rows,
                    { kind: 'totals', assignment },
                    ...(allowsNewTimecards ? [{ kind: 'add' as const, assignment }] : []),
                ];
            }),
        [assignments, sortState, formIndexById, isCredit, allowsNewTimecards]
    );

    const getLineKey = useCallback((index: number) => lineKey(lines[index]), [lines]);

    const isRemoveActionDisabled = isCorrectionInvoice && (isSent || isPaid);
    const selectableIds = useMemo(
        () =>
            isInViewMode || isRemoveActionDisabled
                ? []
                : invoiceMatter.invoiceTimecards
                      .filter(
                          (timecard) =>
                              !timecard.isRemoved &&
                              formIndexById.has(timecard.id) &&
                              invoiceTimecards?.[formIndexById.get(timecard.id) ?? -1]?.editIntentTypeCode == null
                      )
                      .map((timecard) => timecard.id),
        [isInViewMode, isRemoveActionDisabled, invoiceMatter.invoiceTimecards, formIndexById, invoiceTimecards]
    );
    const selectedCount = selectableIds.filter((id) => selectedIds.has(id)).length;
    const isAllSelected = selectedCount > 0 && selectedCount === selectableIds.length;

    const navigateToAdd = (assignment?: InvoiceTimecardAssignment) =>
        navigateWithReturn(
            assignment?.assignmentId
                ? `/invoice/${invoiceId}/matter/${matterId}/assignment/${assignment.assignmentId}/timecards/new`
                : `/invoice/${invoiceId}/matter/${matterId}/timecards/new`,
            {
                state: {
                    returnTo: `${INVOICE_TAB_PATHS.TIMECARDS.replace(':id', String(invoiceId))}#${buildElementKey(assignment)}`,
                    ...createTabState('invoices-center-block', 'timecards'),
                },
            }
        );
    const isAddDisabled = isInViewMode || !canCreateTimecards || buttonsDisabled;

    if (isLoading) {
        return <InvoiceLoader />;
    }

    const showsActions = !isInViewMode || isCorrectionInvoice;
    const columnCount = isNarrow ? 3 : advancedBillingEnabled ? 6 : 5;
    const total = sumAssignments(assignments, isPaymentCurrencyMode);
    const sortField = SORT_FIELDS.find(({ field }) => field === sortState.field);

    const addButton = allowsNewTimecards ? (
        <Tooltip content={t('invoices.tooltips.add-new-timecard')}>
            <Button
                id={buildElementKey()}
                variant="ghost"
                size="sm"
                disabled={isAddDisabled}
                focusableWhenDisabled
                onClick={() => navigateToAdd()}
                className={`${TITLE_ACTION} text-primary`}
            >
                <PlusIcon />
                <FormattedMessage id="timecards.add-timecard" />
            </Button>
        </Tooltip>
    ) : null;

    // Grouped by assignment, each group has its own add row, so the title row needs none.
    const isAllGrouped =
        !isCredit && assignments.length > 0 && assignments.every((assignment) => !!assignment.assignmentId);
    const titleActions =
        invoiceTimecardTotalCount === 0 ? (
            addButton
        ) : (
            <>
                {isAllGrouped ? null : addButton}
                {/* Only where a row can be selected: in view mode nothing can, and the selection could not act. */}
                {selectableIds.length ? (
                    <ItemBulkSelectButton disabled={buttonsDisabled} onClick={table.handleBulkSelect} />
                ) : null}
                <DropdownMenu>
                    <DropdownMenuTrigger render={<Button variant="ghost" size="sm" className={TITLE_ACTION} />}>
                        <SortIcon />
                        <FormattedMessage
                            id="invoices.details.table.sort-by-field"
                            values={{ field: sortField?.field ?? 'none' }}
                        />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-60">
                        <DropdownMenuGroup>
                            <DropdownMenuLabel>
                                <FormattedMessage id="invoices.details.table.sort-by" />
                            </DropdownMenuLabel>
                            <DropdownMenuRadioGroup
                                value={sortState.field}
                                onValueChange={(field: SortField) => setSortState((prev) => ({ ...prev, field }))}
                            >
                                {SORT_FIELDS.map(({ field, labelKey }) => (
                                    <DropdownMenuRadioItem key={field} value={field}>
                                        {t(labelKey)}
                                    </DropdownMenuRadioItem>
                                ))}
                            </DropdownMenuRadioGroup>
                        </DropdownMenuGroup>
                        <DropdownMenuSeparator />
                        <DropdownMenuGroup>
                            <DropdownMenuLabel>
                                <FormattedMessage id="invoices.details.table.sort-order" />
                            </DropdownMenuLabel>
                            <DropdownMenuRadioGroup
                                value={sortState.direction}
                                onValueChange={(direction: TimecardSortState['direction']) =>
                                    setSortState((prev) => ({ ...prev, direction }))
                                }
                            >
                                <DropdownMenuRadioItem value="asc">
                                    <FormattedMessage id="invoices.details.table.sort-ascending" />
                                </DropdownMenuRadioItem>
                                <DropdownMenuRadioItem value="desc">
                                    <FormattedMessage id="invoices.details.table.sort-descending" />
                                </DropdownMenuRadioItem>
                            </DropdownMenuRadioGroup>
                        </DropdownMenuGroup>
                    </DropdownMenuContent>
                </DropdownMenu>
                <AdvancedBillingToggle
                    checked={advancedBillingEnabled}
                    onCheckedChange={setAdvancedBillingEnabled}
                    disabled={isInViewMode}
                />
            </>
        );

    const { areIconsDisabled } = actions;
    const selectionActions = (
        <>
            {actions.isUserPrivilegedToUpdateHourlyRate ? (
                <Button
                    variant="outline"
                    size="sm"
                    disabled={areIconsDisabled}
                    onClick={actions.openBulkPriceChangeModal}
                >
                    <FormattedMessage id="invoices.tooltips.change-price-in-bulk" />
                </Button>
            ) : null}
            <Button variant="outline" size="sm" disabled={areIconsDisabled} onClick={actions.openSwitchMatterModal}>
                <FormattedMessage id="invoices.tooltips.move-to-another-case" />
            </Button>
            {isCorrectionInvoice ? (
                <Button
                    variant="outline"
                    size="sm"
                    disabled={areIconsDisabled}
                    onClick={() => void table.handleRemove()}
                >
                    <FormattedMessage id="invoices.tooltips.remove-via-correction" />
                </Button>
            ) : (
                <FeatureFlagged action="show" flag={FeatureFlag.DISCARD_TIMECARDS_EXPENSES_ON_INVOICE}>
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={areIconsDisabled}
                        onClick={actions.openBulkDiscardConfirmationModal}
                    >
                        <FormattedMessage id="invoices.tooltips.discard-timecards" />
                    </Button>
                </FeatureFlagged>
            )}
            <DropdownMenu>
                <DropdownMenuTrigger render={<Button variant="outline" size="sm" disabled={areIconsDisabled} />}>
                    <FormattedMessage id="tabs.more" />
                    <ChevronDownIcon />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                    <DropdownMenuItem onClick={() => void table.handlePostpone()}>
                        <FormattedMessage id="invoices.tooltips.postpone" />
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        disabled={table.hasNonBillableSelected}
                        onClick={() => void table.handleNonBillable()}
                    >
                        <FormattedMessage id="invoices.tooltips.mark-as-non-billable" />
                    </DropdownMenuItem>
                    <DropdownMenuItem disabled={table.hasBillableSelected} onClick={() => void table.handleBillable()}>
                        <FormattedMessage id="invoices.tooltips.mark-as-billable" />
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={actions.openBulkCapModal}>
                        <FormattedMessage id="invoices.tooltips.change-amount-in-bulk" />
                    </DropdownMenuItem>
                    {allowsNewTimecards ? (
                        <DropdownMenuItem
                            disabled={actions.isMultipleSelection || !canCreateTimecards}
                            onClick={table.handleDuplicate}
                        >
                            <FormattedMessage id="invoices.tooltips.duplicate" />
                        </DropdownMenuItem>
                    ) : null}
                </DropdownMenuContent>
            </DropdownMenu>
        </>
    );

    const renderLine = (index: number, measureRef: Ref<HTMLTableRowElement>) => {
        const line = lines[index];
        if (line.kind === 'row') {
            const { timecard } = line;
            const formIndex = formIndexById.get(timecard.id) ?? -1;
            return (
                <InvoiceTimecardTableRow
                    key={lineKey(line)}
                    measureRef={measureRef}
                    virtualIndex={index}
                    isNarrow={isNarrow}
                    id={timecard.id}
                    readonly={isInViewMode}
                    index={formIndex}
                    invoiceTimecard={timecard}
                    timecardForm={invoiceTimecards?.[formIndex]}
                    invoiceId={invoiceId}
                    invoice={invoice}
                    matterAssingmentList={invoiceMatter.matterAssignments}
                    classifiers={classifiers}
                    isNew={timecard.timecard ? addedInvoiceTimecardIds.includes(timecard.timecard.id ?? 0) : false}
                    control={control}
                    validateForm={validateForm}
                    setValue={setValue}
                    getValues={getValues}
                    reset={reset}
                    advancedBillingEnabled={advancedBillingEnabled}
                    handleUpdateInvoiceTimecards={(idList, updated) => {
                        void table.handleUpdateInvoiceTimecards(idList, updated);
                    }}
                    shouldOpenModal={timecard.timecard?.id === openModalForTimecardId}
                    clearModalRequest={clearModalRequest}
                    updateInvoiceTimecard={updateInvoiceTimecard}
                    nonBillableBgColor={table.nonBillableBgColor}
                    isCredit={isCredit}
                    isCorrectionInvoice={isCorrectionInvoice}
                    isSent={isSent}
                    isPaid={isPaid}
                    multiplierTypes={table.multiplierTypes}
                    buttonsDisabled={buttonsDisabled}
                    isTwoDecimalDisplayEnabled={table.isTwoDecimalDisplayEnabled}
                    isTwoDecimalEntryEnabled={table.isTwoDecimalEntryEnabled}
                    displayDecimalScale={table.displayDecimalScale}
                    isSelected={selectedIds.has(timecard.id)}
                    toggleSelection={table.toggleSelection}
                />
            );
        }
        const { assignment } = line;
        if (line.kind === 'group') {
            return (
                <tr key={lineKey(line)} ref={measureRef} data-index={index} className="bg-surface-tile">
                    <td colSpan={columnCount} className="p-0">
                        <InvoiceAssignmentTimecardHeader
                            isInViewMode={isInViewMode}
                            index={invoiceMatter.invoiceAssignments.indexOf(assignment)}
                            assignment={assignment}
                            invoice={invoice}
                            getValues={getValues}
                            setValue={setValue}
                            control={control}
                            invoiceMatter={invoiceMatter}
                        />
                    </td>
                </tr>
            );
        }
        if (line.kind === 'totals') {
            return (
                <TotalsRow
                    key={lineKey(line)}
                    measureRef={measureRef}
                    index={index}
                    label={t('invoices.details.totals')}
                    totals={sumAssignments([assignment], isPaymentCurrencyMode)}
                    currency={displayCurrency}
                    isNarrow={isNarrow}
                    hasAdvancedColumn={advancedBillingEnabled}
                />
            );
        }
        return (
            <tr key={lineKey(line)} ref={measureRef} data-index={index} className="border-divider border-b">
                <td />
                <td colSpan={columnCount - 1} className="px-2 py-1.5">
                    <Button
                        id={buildElementKey(assignment)}
                        variant="ghost"
                        size="sm"
                        aria-label={t('invoices.details.add-timecard-to-assignment', {
                            assignment: assignment.assignmentName ?? '',
                        })}
                        disabled={isAddDisabled}
                        onClick={() => navigateToAdd(assignment)}
                        className={`${TITLE_ACTION} text-primary -ms-2`}
                    >
                        <PlusIcon />
                        <FormattedMessage id="timecards.add-timecard" />
                    </Button>
                </td>
            </tr>
        );
    };

    return (
        <ItemCard
            cardRef={cardRef}
            titleId={titleId}
            title={t('timecards.timecards')}
            count={invoiceTimecardTotalCount}
            caseLabel={<CaseLabel number={invoiceMatter.matterNumber} name={invoiceMatter.matterName} />}
            titleExtras={
                // The rates show whether or not the price list has a name; with neither, nothing does.
                <span className="text-meta text-foreground-tertiary flex min-w-0 items-center gap-1.5 empty:hidden">
                    {invoiceMatter.pricelistName ? (
                        <>
                            <FormattedMessage id="invoices.details.assignments.price-list" />
                            <span className="text-foreground font-medium">{invoiceMatter.pricelistName}</span>
                        </>
                    ) : null}
                    <CasePricelistRangeTooltip
                        matterId={invoiceMatter.matterId}
                        rates={invoiceMatter.rates}
                        stored={invoiceMatter}
                        pricelistCurrencyTypeCode={invoiceMatter.pricelistCurrencyTypeCode}
                    />
                </span>
            }
            actions={titleActions}
            selectedCount={showsActions ? selectedCount : 0}
            selectedMessage={intl.formatMessage(
                { id: 'invoices.details.timecards-selected' },
                { count: selectedCount }
            )}
            selectionLabel={t('invoices.details.timecards-selection-actions')}
            selectionActions={selectionActions}
            emptyMessage={<FormattedMessage id="invoices.details.no-timecards" />}
        >
            <ItemTable
                caption={t('invoices.details.timecards-caption', {
                    case: `${invoiceMatter.matterNumber} ${invoiceMatter.matterName}`,
                })}
                columnCount={columnCount}
                rowCount={lines.length}
                getRowKey={getLineKey}
                renderRow={renderLine}
                head={
                    <ItemHeadCells
                        selectAll={
                            selectableIds.length ? (
                                <Checkbox
                                    aria-label={t('invoices.details.table.select-all-timecards')}
                                    checked={isAllSelected}
                                    indeterminate={selectedCount > 0 && !isAllSelected}
                                    disabled={buttonsDisabled}
                                    onCheckedChange={() =>
                                        isAllSelected ? clearSelection() : onBulkSelect(true, selectableIds)
                                    }
                                />
                            ) : null
                        }
                        entryLabelId="timecards.timecard"
                        figureLabelId="invoices.details.table.hours-and-rate"
                        isFigureEnd
                        hasAdvancedColumn={advancedBillingEnabled}
                        currency={displayCurrency}
                        isNarrow={isNarrow}
                    />
                }
                foot={
                    <TotalsRow
                        label={t('invoices.totals-tab.table-headings.total')}
                        totals={total}
                        currency={displayCurrency}
                        isNarrow={isNarrow}
                        hasAdvancedColumn={advancedBillingEnabled}
                    />
                }
            />
        </ItemCard>
    );
}

/** A totals row: an assignment's, in the body, or the card's, closing the table. */
function TotalsRow({
    measureRef,
    index,
    label,
    totals,
    currency,
    isNarrow,
    hasAdvancedColumn,
}: {
    measureRef?: Ref<HTMLTableRowElement>;
    index?: number;
    label: string;
    totals: ReturnType<typeof sumAssignments>;
    currency: string;
    isNarrow: boolean;
    hasAdvancedColumn: boolean;
}) {
    const billed = <HoursDisplay value={totals.billed} />;
    const amount = <MoneyDisplay amount={totals.amount} currency={currency} />;
    return (
        <tr ref={measureRef} data-index={index} className={ITEM_TOTALS_ROW}>
            <th scope="row" colSpan={2} className="text-meta py-3 ps-5 pe-2 text-start font-semibold">
                {isNarrow ? (
                    <span className="flex items-baseline justify-between gap-3">
                        {label}
                        <span className="text-body1 tabular-nums">
                            {billed} · {amount}
                        </span>
                    </span>
                ) : (
                    label
                )}
            </th>
            {isNarrow ? null : (
                <>
                    <td className="px-2 py-3 text-end">
                        <div className={ITEM_FIGURE}>{billed}</div>
                        <div className={ITEM_SUB_FIGURE}>
                            <FormattedMessage
                                id="invoices.details.table.used-hours"
                                values={{ hours: <HoursDisplay value={totals.used} /> }}
                            />
                        </div>
                    </td>
                    {hasAdvancedColumn ? <td /> : null}
                    <td className={`${ITEM_FIGURE} px-2 py-3 text-end`}>{amount}</td>
                </>
            )}
            <td />
        </tr>
    );
}
